// SceneLandmarks — maps planetSections to landmark meshes. For each
// scene with landmarkType !== 'none' && !== 'path', render the
// corresponding landmark at scene.landmarkPos. All landmarks orient
// to the planet's up vector at their position.
//
// Hover: small scale-up + color highlight. Selected (when active
// scene is this one): larger glow + label appears. Click: sets
// openPanelId to the scene's panel key via the supplied callback.

import { useMemo, useRef, type ReactNode } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { Group } from 'three';

import { SCENES, type SceneConfig, type LandmarkType } from './planetSections';
import { openDetail } from './SatelliteInteraction';
import { House } from './landmarks/House';
import { Workshop } from './landmarks/Workshop';
import { SkillStones } from './landmarks/SkillStones';
import { BookFragments } from './landmarks/BookFragments';
import { SignalTower } from './landmarks/SignalTower';
import { VISTA_SCENE_IDS } from './SurfaceVistas';
import { projectToPlanetSurface } from './Planet';

// Landmarks past this camera distance drop their decorative elements
// (chimney, window glow, antenna, point light) and switch to a single
// silhouette mesh. Tied to the spec's "if camera distance to them is
// > 6, swap to a single mesh version" rule.
const LANDMARK_LOD_DISTANCE = 6;

export interface SceneLandmarksProps {
  /** Active scene index (0..6). */
  activeSceneIndex: number;
  /** Per-landmark hover state — set by the interactive group. */
  hoveredLandmarkKey?: string | null;
  /** Called when a landmark is clicked. */
  onLandmarkClick?: (scene: SceneConfig) => void;
}

/** Choose the right landmark component for a landmark type. */
function renderLandmark(
  scene: SceneConfig,
  isActive: boolean,
  isHovered: boolean,
  groupRef: React.MutableRefObject<Group | null>,
) {
  // The landmark is placed at scene.landmarkPos (already in world
  // space relative to the planet). The orientation aligns the
  // landmark's +Y with the planet's "up" at that point — i.e. the
  // outward normal of the planet surface.
  const [x, y, z] = scene.landmarkPos;
  // LOD: if the scene's framed camera distance to the landmark is
  // greater than the simplify threshold, render the silhouette-only
  // (single-mesh) version. The scene.cameraPos comes from
  // planetSections and is what the narrative-mode camera rig
  // actually settles at, so this is a close-enough proxy for real
  // distance.
  const [cx, cy, cz] = scene.cameraPos;
  const cameraDistance = Math.hypot(cx - x, cy - y, cz - z);
  const isFar = cameraDistance > LANDMARK_LOD_DISTANCE;

  switch (scene.landmarkType as LandmarkType) {
    case 'house':
      return (
        <SurfaceAnchor key={scene.id} scene={scene} groupRef={groupRef}>
          <House isActive={isActive} isHovered={isHovered} isFar={isFar} />
        </SurfaceAnchor>
      );
    case 'workshop':
      return (
        <SurfaceAnchor key={scene.id} scene={scene} groupRef={groupRef}>
          <Workshop isActive={isActive} isHovered={isHovered} isFar={isFar} />
        </SurfaceAnchor>
      );
    case 'skill_stones':
      return (
        <SurfaceAnchor key={scene.id} scene={scene} groupRef={groupRef}>
          <SkillStones isActive={isActive} isHovered={isHovered} />
        </SurfaceAnchor>
      );
    case 'book_fragments':
      return (
        <SurfaceAnchor key={scene.id} scene={scene} groupRef={groupRef}>
          <BookFragments isActive={isActive} isHovered={isHovered} />
        </SurfaceAnchor>
      );
    case 'signal_tower':
      return (
        <SurfaceAnchor key={scene.id} scene={scene} groupRef={groupRef}>
          <SignalTower isActive={isActive} isHovered={isHovered} />
        </SurfaceAnchor>
      );
    default:
      return null;
  }
}

function SurfaceAnchor({
  scene,
  groupRef,
  children,
}: {
  scene: SceneConfig;
  groupRef: React.MutableRefObject<Group | null>;
  children: ReactNode;
}) {
  const anchorRef = useRef<Group | null>(null);
  const rotation = useMemo(() => {
    const outward = new THREE.Vector3(...scene.landmarkPos).normalize();
    return new THREE.Euler().setFromQuaternion(
      new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), outward),
    );
  }, [scene.landmarkPos]);

  useFrame((state) => {
    const planetRotation =
      state.scene.getObjectByName('planet')?.rotation.y ?? scene.planetRotation;
    const point = projectToPlanetSurface(scene.landmarkPos, planetRotation, -0.012);
    anchorRef.current?.position.set(point[0], point[1], point[2]);
  });

  return (
    <group
      ref={(node) => {
        anchorRef.current = node;
        groupRef.current = node;
      }}
      rotation={rotation}
      name={`surface-anchor-${scene.key}`}
    >
      {children}
    </group>
  );
}

export function SceneLandmarks({
  activeSceneIndex,
  hoveredLandmarkKey,
  onLandmarkClick,
}: SceneLandmarksProps) {
  // One ref per scene that has a landmark.
  const refs = useRef<Record<number, React.MutableRefObject<Group | null>>>({});

  // Prefer a SurfaceVista when its chapter is active; otherwise keep the
  // simpler landmark so the planet still has quiet markers from afar.
  const scenesWithLandmarks = useMemo(
    () =>
      SCENES.filter((s) => {
        if (s.landmarkType === 'none' || s.landmarkType === 'path') return false;
        if (VISTA_SCENE_IDS.has(s.id) && s.id === activeSceneIndex) return false;
        return true;
      }),
    [activeSceneIndex],
  );

  // Attach pointer handlers to each landmark group via the ref.
  const setRef = (id: number) => {
    if (!refs.current[id]) {
      refs.current[id] = { current: null };
    }
    return refs.current[id];
  };

  return (
    <group name="scene-landmarks">
      {scenesWithLandmarks.map((scene) => {
        const isActive = scene.id === activeSceneIndex;
        const isHovered = hoveredLandmarkKey === scene.key;
        const groupRef = setRef(scene.id);
        return (
          <group
            key={scene.id}
            onPointerOver={(e) => {
              e.stopPropagation();
              if (typeof window !== 'undefined') {
                window.__planetHoveredLandmark = scene.key;
              }
              document.body.style.cursor = 'pointer';
            }}
            onPointerOut={(e) => {
              e.stopPropagation();
              if (typeof window !== 'undefined') {
                window.__planetHoveredLandmark = null;
              }
              document.body.style.cursor = '';
            }}
            onClick={(e) => {
              e.stopPropagation();
              // Exploration mode: fly to the landmark, show info card.
              if (
                typeof window !== 'undefined' &&
                window.__planetMode === 'exploration'
              ) {
                if (window.__planetShowInfoCard && groupRef.current) {
                  const wp = new THREE.Vector3();
                  groupRef.current.getWorldPosition(wp);
                  window.__planetShowInfoCard({
                    kind: 'landmark',
                    id: scene.landmarkType as string,
                    title: scene.name,
                    blurb: scene.subtitle,
                    worldPos: [wp.x, wp.y, wp.z],
                  });
                }
                return;
              }
              // Map the scene key to a deep-dive detail type. Each
              // landmark corresponds to one of the chapter panels, so
              // clicking it opens the matching detail view.
              const detailTypeForKey: Record<string, 'about' | 'skills' | 'milestone' | 'contact' | 'project' | 'note'> = {
                about: 'about',
                skills: 'skills',
                projects: 'project',
                notes: 'note',
                timeline: 'milestone',
                contact: 'contact',
              };
              const type = detailTypeForKey[scene.key] ?? 'about';
              // For 'project' / 'note' / 'milestone' we need an id. The
              // project/note landmark opens a list of satellites/notes,
              // so we leave the detail panel closed for now and let
              // the consumer pick. For 'milestone' we open the first
              // year (the consumer can navigate further from there).
              let id = scene.key;
              if (type === 'milestone') {
                // Open the first timeline milestone. The user can
                // navigate through others from the timeline panel.
                id = '2019';
              } else if (type === 'project') {
                // Clicking the workshop landmark opens the project list.
                // We don't open the detail panel; the right-rail
                // SatelliteInfoCard handles the project list.
                openDetail(null);
                onLandmarkClick?.(scene);
                return;
              } else if (type === 'note') {
                openDetail(null);
                onLandmarkClick?.(scene);
                return;
              }
              openDetail({ type, id });
            }}
            userData={{ landmarkType: scene.landmarkType, sceneId: scene.id }}
          >
            {renderLandmark(scene, isActive, isHovered, groupRef)}
          </group>
        );
      })}
    </group>
  );
}

export default SceneLandmarks;

// Augment the global Window type to include the hover ref.
declare global {
  interface Window {
    __planetHoveredLandmark?: string | null;
  }
}
